<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import historicHomeEnergyEfficiency1 from './images/historicHomeEnergyEfficiency.jpg';
	import historicHomeEnergyEfficiency2 from './images/historicHomeEnergyEfficiency2.jpg';
	import historicHomeEnergyEfficiency3 from './images/historicHomeEnergyEfficiency3.jpg';
	import historicHomeEnergyEfficiency4 from './images/historicHomeEnergyEfficiency4.jpg';
</script>

<Section>
	<p>
		Aileen’s mission was to boost historic home energy efficiency for her
		Seattle house. She got rid of her oil furnace and electric baseboards. She
		air sealed and insulated. She even participated in an energy efficiency
		study by the US Department of Energy. But she wanted to go further and the
		one element that remained unaddressed in her historic home energy efficiency
		project was incredibly important: her windows.
	</p>
	<Image
		src={historicHomeEnergyEfficiency1}
		alt="interior view of indow window inserts for historic home energy efficiency"
	/>
	<p>
		Aileen began searching for window efficiency ideas and was impressed when
		she learned that Indow window inserts could provide as much comfort and
		energy efficiency as high-end replacement windows. Indow is AERC certified.
		AERC, the <a
			href="https://aercenergyrating.org/product-search/residential-product-search"
			rel="external"
			target="_blank">Attachments Energy Rating Council</a
		>, is an unaffiliated organization that rates window attachment products on
		energy efficiency by measuring various factors such as Air Leakage (AL) and
		U-Factor.
	</p>
	<p>
		Aileen decided to put
		<a href="/performance"> energy-efficient Indow inserts </a>
		in 27 of the single-pane windows in her 1916 Seattle home. And it’s made a world
		of difference.
	</p>
</Section>

<Section>
	<h2>Historic Home Energy Efficiency Made Easy</h2>
	<p>
		A follow up
		<a
			href="https://www.pnnl.gov/main/publications/external/technical_reports/PNNL-22855.pdf"
		>
			energy efficiency study
		</a>
		for the U.S. Department of Energy prepared by the Pacific Northwest National Laboratory
		found that the Indow window inserts reduced her heating, ventilation and air-conditioning
		energy use by more than 20%! She would have had to have gotten high-end double
		or triple-pane windows, at great expense, to achieve that level of savings.
	</p>
	<Image
		alt="indow window inserts installed in historic home for energy efficiency"
		src={historicHomeEnergyEfficiency2}
	/>
	<p>
		That’s important when you consider that energy use in residential homes has
		increased to 22 percent of total energy use in the United States. Reducing
		energy loss through windows is one of the most important steps individuals
		can take to address climate change.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonials</h2>
	<div class="flex flex-col gap-2 text-xl italic">
		<p class="text-indow-blue!">
			“You can take a 100-year-old historic home and by doing some effective
			things, you can make it as energy efficient as a new building. The biggest
			difference I noticed was the sound reduction, I’m on a residential street
			but you get a whole lot of noise penetration with single-pane windows.”
		</p>
		<p>– Aileen Gagney, Home Owner, Seattle Washington</p>
	</div>
	<p class="text-indow-blue!">
		<a class="italic" href="/resources/case-studies">
			Read more case studies.
		</a>
	</p>
</Section>

<Section>
	<h2>Additional Benefits: Reducing Window Condensation and Window Noise</h2>
	<p>
		The extensive air sealing Aileen had done as part of an earlier energy
		efficiency upgrade had caused condensation on her single-pane windows. When
		she installed the Indow inserts the <a
			href="/solutions/prevent-window-condensation">condensation disappeared</a
		>. The inserts prevented the warm, moist interior air from coming into
		contact with her cold windows.
	</p>
	<p>
		Not only did it do that and lower her energy use, but it had the unexpected
		benefit of making her home much quieter.
	</p>
	<Image
		src={historicHomeEnergyEfficiency3}
		alt="two windows in historic home with Indow inserts for energy efficiency"
	/>
</Section>

<Section>
	<h2>Historic Home Energy Efficiency Saved $273 a Month</h2>
	<p>
		With all the energy efficiency work taken together Aileen has seen her
		energy bills drop from more than $300 a month to $27 a month. “I’m trying to
		reduce our impact on the environment to save the environment.”
	</p>

	<Image
		src={historicHomeEnergyEfficiency4}
		alt="closeup of Indow window insert used for energy efficiency in historic home"
	/>
</Section>

<GetAFreeEstimate />
